/**
 * Song Questions — Firestore I/O. Pure logic lives in songQuestionsModel.js.
 *
 * THE FIRST FAMILY-AUTHORED CONTENT A STAFF MEMBER READS. Everything a family
 * could write before this went to their own account (Account settings) or a
 * scoped booking flow (cancel a lesson, redeem a makeup). This is the first
 * write path that produces content a teacher opens during a lesson, so the
 * create path is the security-interesting one and is deliberately narrow: a
 * customer may create a question about THEIR OWN child and may not set its
 * status, its resolution, or its author.
 *
 * RULES ARE NOT FILTERS. The customer read branch in firestore.rules requires a
 * non-null `studentId` whose student doc resolves to the caller's `familyId`.
 * Every customer query below therefore constrains `studentId ==` itself; an
 * unconstrained list is denied whole, not silently trimmed.
 *
 * COMPOSITE INDEXES, and this is a DEPLOY-CHECKLIST ITEM RATHER THAN A TEST
 * OBLIGATION. Both queries here pair an equality filter with an orderBy on a
 * different field, which production requires a composite index for. THE
 * EMULATOR SERVES THEM WITHOUT ONE, so `npm run test:rules` is green either way
 * and no test can catch an absent index — prod simply 400s on the first read.
 * Two are committed in firestore.indexes.json with this file:
 *   songQuestions: studentId ASC, createdAt DESC
 *   songQuestions: studentId ASC, status ASC, createdAt DESC
 * `firestore.rules` documents the same trap for `documents`; it has bitten this
 * codebase before.
 */

import { db } from '../firebase';
import {
	collection,
	query,
	where,
	orderBy,
	limit,
	getDocs,
	addDoc,
	doc,
	updateDoc,
	deleteDoc,
	serverTimestamp,
	Timestamp,
} from 'firebase/firestore';
import {
	SONG_QUESTION_STATUS,
	normalizeQuestionText,
	QUESTION_MAX_LENGTH,
} from './songQuestionsModel';

const COLLECTION_NAME = 'songQuestions';

const mapSnapshot = (snapshot) => snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));

/**
 * Every question about one student, newest first — the customer surface and the
 * teacher's full history.
 *
 * ONE QUERY PER STUDENT, never one per family, mirroring the Song Notes read.
 * Family membership lives on `students.familyId` and is resolved by the rule
 * through a get(); it is never denormalized onto a question, because a copied
 * familyId goes stale the day a student moves families.
 */
export const fetchQuestionsForStudent = async (studentId, max = 200) => {
	if (!studentId) return [];
	const q = query(
		collection(db, COLLECTION_NAME),
		where('studentId', '==', studentId),
		orderBy('createdAt', 'desc'),
		limit(max)
	);
	return mapSnapshot(await getDocs(q));
};

/**
 * Only the unresolved ones — what the Lesson Dashboard notes column renders.
 *
 * Ordered createdAt ASC because the caller wants the question that has waited
 * longest first (see partitionQuestions). Needs its own composite index; see
 * the header note.
 */
export const fetchOpenQuestionsForStudent = async (studentId, max = 50) => {
	if (!studentId) return [];
	const q = query(
		collection(db, COLLECTION_NAME),
		where('studentId', '==', studentId),
		where('status', '==', SONG_QUESTION_STATUS.OPEN),
		orderBy('createdAt', 'asc'),
		limit(max)
	);
	return mapSnapshot(await getDocs(q));
};

/**
 * Ask a question. Always born `open` — a family cannot create a resolved one,
 * and the rule enforces that rather than trusting this function.
 *
 * `songNoteId` is optional and is what separates "a question about the song"
 * from "a question about one of our notes" — the second being the more useful
 * of the two in a lesson, since it tells the teacher exactly where to start.
 * The note's TEXT is deliberately not copied here: see noteForQuestion() in the
 * model for why the join happens in memory instead.
 */
export const createSongQuestion = async ({
	studentId,
	songId,
	songName = '',
	songNoteId = null,
	text,
	askedByUid,
	askedByName = '',
}) => {
	const body = normalizeQuestionText(text);
	if (!studentId || !songId || !body) {
		throw new Error('createSongQuestion requires studentId, songId and non-empty text');
	}
	if (!askedByUid) {
		throw new Error('createSongQuestion requires askedByUid — questions are always attributed');
	}
	if (body.length > QUESTION_MAX_LENGTH) {
		throw new Error(`Question is too long (${body.length}/${QUESTION_MAX_LENGTH})`);
	}
	// Same guard as createSongNote: a board-transient id can never be a
	// repertoire doc id, so a question persisted against one is born orphaned —
	// it would never group onto a song card on either surface.
	if (/^(local|pending)-/.test(songId)) {
		throw new Error(
			`createSongQuestion refuses transient songId "${songId}" — resolve a repertoire row first`
		);
	}

	const payload = {
		studentId,
		songId,
		songName: songName || '',
		songNameLower: (songName || '').trim().toLowerCase(),
		songNoteId: songNoteId || null,
		text: body,
		status: SONG_QUESTION_STATUS.OPEN,
		askedByUid,
		askedByName: askedByName || '',
		resolvedAt: null,
		resolvedByUid: null,
		createdAt: serverTimestamp(),
		updatedAt: serverTimestamp(),
	};

	const ref = await addDoc(collection(db, COLLECTION_NAME), payload);
	// Echo a locally-usable row: serverTimestamp() resolves to null on the client
	// until the write round-trips, and both surfaces sort on createdAt.
	const now = Timestamp.now();
	return { id: ref.id, ...payload, createdAt: now, updatedAt: now };
};

/**
 * Mark a question dealt with. TEACHER-ONLY by rule (Anthony, 2026-08-12: "the
 * teacher clears it") — a family cannot resolve its own question, because
 * "answered" is a claim about the lesson, not about the asker.
 */
export const resolveSongQuestion = async (questionId, { byUid = null } = {}) => {
	if (!questionId) throw new Error('resolveSongQuestion requires a question id');
	await updateDoc(doc(db, COLLECTION_NAME, questionId), {
		status: SONG_QUESTION_STATUS.RESOLVED,
		resolvedAt: serverTimestamp(),
		resolvedByUid: byUid || null,
		updatedAt: serverTimestamp(),
	});
};

/**
 * Undo a resolve. Exists because clearing is a one-tap action taken mid-lesson,
 * which is exactly the circumstance in which the wrong row gets tapped.
 */
export const reopenSongQuestion = async (questionId) => {
	if (!questionId) throw new Error('reopenSongQuestion requires a question id');
	await updateDoc(doc(db, COLLECTION_NAME, questionId), {
		status: SONG_QUESTION_STATUS.OPEN,
		resolvedAt: null,
		resolvedByUid: null,
		updatedAt: serverTimestamp(),
	});
};

/** Staff-only hard delete, for content that should not have been written. */
export const deleteSongQuestion = async (questionId) => {
	if (!questionId) throw new Error('deleteSongQuestion requires a question id');
	await deleteDoc(doc(db, COLLECTION_NAME, questionId));
};

const SongQuestionsService = {
	fetchQuestionsForStudent,
	fetchOpenQuestionsForStudent,
	createSongQuestion,
	resolveSongQuestion,
	reopenSongQuestion,
	deleteSongQuestion,
};

export default SongQuestionsService;
